import React, { useEffect, useMemo, useRef } from 'react';

import flagsmith from '@flagsmith/flagsmith';
import {
  FlagsmithProvider as Provider,
  useFlags,
  useFlagsmith,
  useFlagsmithLoading,
} from '@flagsmith/flagsmith/react';
import { useSelector } from 'react-redux';

import { getFlagsmithConfig } from './config';

// The app reducer is injected after the root provider first renders. Return a
// non-root fallback until that happens so React Redux does not warn or expose
// an undefined user to the identity component.
const selectUser = (state: any) => state?.app?.user ?? null;

const flagsmithConfig = getFlagsmithConfig();

const flagsmithOptions =
  flagsmithConfig.environmentID && flagsmithConfig.api
    ? {
        environmentID: flagsmithConfig.environmentID,
        api: flagsmithConfig.api,
        cacheFlags: true,
        onError: (error: { message?: string }) => {
          console.warn('Flagsmith failed to retrieve feature flags', error);
        },
      }
    : undefined;

const FlagsmithIdentity = () => {
  const client = useFlagsmith();
  const loadingState = useFlagsmithLoading();
  const user = useSelector(selectUser);
  const previousIdentity = useRef<string | undefined>(undefined);

  const identity = useMemo(() => {
    if (!user?.authenticated || !user.info?.id) return '';

    return `${user.info.org_id}:${user.info.id}`;
  }, [user?.authenticated, user?.info?.id, user?.info?.org_id]);

  const traits = useMemo(
    () => ({
      organization: user?.info?.org_id,
      username: user?.info?.username,
      email: user?.info?.email,
    }),
    [user?.info?.email, user?.info?.org_id, user?.info?.username],
  );

  useEffect(() => {
    if (!flagsmithOptions || !client.initialised) return;

    if (!identity) {
      if (previousIdentity.current) {
        client.logout().catch(error => {
          console.warn('Flagsmith failed to clear the user identity', error);
        });
        previousIdentity.current = undefined;
      }
      return;
    }

    if (previousIdentity.current === identity) return;

    previousIdentity.current = identity;
    client.identify(identity, traits).catch(error => {
      previousIdentity.current = undefined;
      console.warn('Flagsmith failed to identify the current user', error);
    });
  }, [client, identity, loadingState?.isLoading, traits]);

  return null;
};

export const FeatureFlagsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => (
  <Provider flagsmith={flagsmith} options={flagsmithOptions}>
    <FlagsmithIdentity />
    {children}
  </Provider>
);

export { useFlags, useFlagsmith, useFlagsmithLoading };
